import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { COUNTRIES, FLAGS } from "@/lib/utils";
import { COUNTRY_TO_DIAL } from "@/lib/dialCodes";
import { formatApiError } from "@/lib/api";
import Avatar from "@/components/Avatar";
import PhoneInput from "@/components/PhoneInput";
import { Check, Camera, Trash2, Lock, Mail, AlertTriangle, Eye, EyeOff, Compass } from "lucide-react";
import { TakeAppTourButton } from "@/components/OnboardingTour";

// Resize + compress an image client-side to keep avatars small
function resizeImage(file, maxSize = 320) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Profile() {
  const { user, updateProfile, changePassword, changeEmail, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    country: user?.country || "",
    company_name: user?.company_name || "",
    avatar: user?.avatar || "",
  });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function onPickFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError("");
    if (!file.type.startsWith("image/")) {
      setUploadError("Please choose an image file (PNG, JPG, WebP).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("Image is too large (max 5 MB).");
      return;
    }
    try {
      const dataUrl = await resizeImage(file, 320);
      set("avatar", dataUrl);
    } catch {
      setUploadError("Could not read that image. Try another one.");
    } finally {
      e.target.value = "";
    }
  }

  function removeAvatar() {
    set("avatar", "");
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setDone(false);
    try {
      await updateProfile(form);
      setDone(true);
      setTimeout(() => setDone(false), 2000);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto">
      <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold">Profile</p>
      <h1 className="text-4xl font-bold text-neutral-900 mt-2">Account settings</h1>
      <p className="text-neutral-500 mt-2">Manage your personal information and preferences.</p>

      <div className="soft-card p-8 mt-8 flex items-center gap-6 flex-wrap">
        <div className="relative">
          <Avatar name={user?.name} src={form.avatar} size={88} />
          <button
            type="button"
            data-testid="prof-avatar-upload"
            onClick={() => fileRef.current?.click()}
            className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-propera hover:bg-propera-hover text-white flex items-center justify-center shadow-lg transition"
            aria-label="Upload profile picture"
          >
            <Camera size={16} />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={onPickFile}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-lg text-neutral-900 truncate">{user?.name}</p>
          <p className="text-sm text-neutral-500 truncate">{user?.email}</p>
          <span className="inline-flex items-center gap-1 mt-2 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-propera-light text-propera">
            {user?.role}
          </span>
          {uploadError && <p className="text-xs text-red-600 mt-2">{uploadError}</p>}
          <p className="text-xs text-neutral-400 mt-2">PNG, JPG or WebP · up to 5MB</p>
          {form.avatar && (
            <button
              type="button"
              data-testid="prof-avatar-remove"
              onClick={removeAvatar}
              className="text-xs text-red-500 hover:text-red-700 mt-1.5 inline-flex items-center gap-1"
            >
              <Trash2 size={12} /> Remove photo
            </button>
          )}
        </div>
      </div>

      <form onSubmit={save} className="soft-card p-8 mt-6 space-y-4">
        <Field label="Full Name" testId="prof-name" value={form.name} onChange={(v) => set("name", v)} />
        <PhoneInput
          testId="prof-phone"
          label="Phone Number"
          value={form.phone}
          onChange={(v) => set("phone", v)}
          defaultDial={COUNTRY_TO_DIAL[form.country] || "+234"}
        />
        {user?.role === "developer" && (
          <Field label="Company Name" testId="prof-company" value={form.company_name} onChange={(v) => set("company_name", v)} />
        )}
        <div>
          <label className="text-xs font-semibold text-neutral-700">Country</label>
          <select
            data-testid="prof-country"
            value={form.country}
            onChange={(e) => set("country", e.target.value)}
            className="mt-1.5 w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition bg-white"
          >
            <option value="">Select</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{FLAGS[c]} {c}</option>)}
          </select>
        </div>
        <div className="flex items-center justify-end gap-3">
          {done && <span className="text-sm text-propera font-medium flex items-center gap-1"><Check size={14} /> Saved</span>}
          <button data-testid="prof-save" type="submit" disabled={saving} className="bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-2.5 font-semibold transition disabled:opacity-60">
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </form>

      <ChangeEmailCard user={user} changeEmail={changeEmail} />
      <ChangePasswordCard changePassword={changePassword} />

      <div className="soft-card p-8 mt-6" data-testid="prof-tour-card">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-propera-light text-propera flex items-center justify-center flex-shrink-0">
            <Compass size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-neutral-900">App Tour</p>
            <p className="text-sm text-neutral-500 mt-0.5">
              New to Propera or need a refresher? Replay the guided walkthrough of every core feature — takes about a minute.
            </p>
          </div>
          <TakeAppTourButton />
        </div>
      </div>

      <DeleteAccountCard user={user} deleteAccount={deleteAccount} onDeleted={() => navigate("/login", { replace: true })} />
    </div>
  );
}

function ChangeEmailCard({ user, changeEmail }) {
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr(""); setMsg(""); setBusy(true);
    try {
      const res = await changeEmail(newEmail, pwd);
      setMsg(`Verification link sent to ${res.email}. Tap it to activate the new address.`);
      setNewEmail(""); setPwd("");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="soft-card p-8 mt-6" data-testid="prof-change-email-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-propera-light text-propera flex items-center justify-center flex-shrink-0">
          <Mail size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-neutral-900">Email address</p>
          <p className="text-sm text-neutral-500 mt-0.5 break-all">{user?.email}</p>
        </div>
        {!open && (
          <button data-testid="prof-open-change-email" onClick={() => setOpen(true)} className="text-sm font-semibold text-propera hover:underline">Change</button>
        )}
      </div>
      {open && (
        <form onSubmit={submit} className="mt-5 space-y-3">
          <Field label="New email" testId="prof-new-email" value={newEmail} onChange={setNewEmail} type="email" />
          <PasswordField label="Current password" testId="prof-email-current-pwd" value={pwd} onChange={setPwd} />
          <p className="text-xs text-neutral-500">We&apos;ll send a verification link to the new address. Your current email keeps working until you verify.</p>
          {err && <p data-testid="prof-email-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{err}</p>}
          {msg && <p data-testid="prof-email-success" className="text-sm text-propera bg-propera-light border border-propera/20 rounded-xl px-3 py-2">{msg}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => { setOpen(false); setErr(""); setMsg(""); }} className="text-sm text-neutral-600 hover:text-neutral-900 px-4 py-2">Cancel</button>
            <button data-testid="prof-submit-change-email" type="submit" disabled={busy || !newEmail} className="bg-propera hover:bg-propera-hover text-white rounded-full px-5 py-2 font-semibold text-sm transition disabled:opacity-60">
              {busy ? "Sending..." : "Send verification link"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function ChangePasswordCard({ changePassword }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr(""); setDone(false);
    if (next !== confirm) { setErr("New passwords don't match"); return; }
    if (next.length < 8) { setErr("New password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await changePassword(current, next);
      setDone(true);
      setCurrent(""); setNext(""); setConfirm("");
      setTimeout(() => { setDone(false); setOpen(false); }, 1800);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="soft-card p-8 mt-6" data-testid="prof-change-password-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-propera-light text-propera flex items-center justify-center flex-shrink-0">
          <Lock size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-neutral-900">Password</p>
          <p className="text-sm text-neutral-500 mt-0.5">Change your password. Minimum 8 characters.</p>
        </div>
        {!open && (
          <button data-testid="prof-open-change-password" onClick={() => setOpen(true)} className="text-sm font-semibold text-propera hover:underline">Change</button>
        )}
      </div>
      {open && (
        <form onSubmit={submit} className="mt-5 space-y-3">
          <PasswordField label="Current password" testId="prof-current-pwd" value={current} onChange={setCurrent} />
          <PasswordField label="New password" testId="prof-new-pwd" value={next} onChange={setNext} />
          <PasswordField label="Confirm new password" testId="prof-confirm-pwd" value={confirm} onChange={setConfirm} />
          {err && <p data-testid="prof-pwd-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{err}</p>}
          {done && <p data-testid="prof-pwd-success" className="text-sm text-propera bg-propera-light border border-propera/20 rounded-xl px-3 py-2 flex items-center gap-1"><Check size={14}/> Password updated</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => { setOpen(false); setErr(""); }} className="text-sm text-neutral-600 hover:text-neutral-900 px-4 py-2">Cancel</button>
            <button data-testid="prof-submit-change-password" type="submit" disabled={busy || !current || !next || !confirm} className="bg-propera hover:bg-propera-hover text-white rounded-full px-5 py-2 font-semibold text-sm transition disabled:opacity-60">
              {busy ? "Updating..." : "Update password"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function DeleteAccountCard({ user, deleteAccount, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const requiresPwd = !!user?.email && user?.signup_method !== "google"; // best-effort — server still enforces

  async function submit(e) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await deleteAccount(pwd || undefined, confirmText);
      onDeleted();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-6 rounded-3xl border-2 border-red-200 bg-red-50/40 p-8" data-testid="prof-delete-account-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
          <AlertTriangle size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-red-800">Delete account</p>
          <p className="text-sm text-red-700/80 mt-0.5">
            Permanently remove your Propera account and all associated data. This cannot be undone.
          </p>
        </div>
        {!open && (
          <button data-testid="prof-open-delete-account" onClick={() => setOpen(true)} className="text-sm font-semibold text-red-700 hover:text-red-900 hover:underline">Delete</button>
        )}
      </div>
      {open && (
        <form onSubmit={submit} className="mt-5 space-y-3">
          <div className="rounded-xl bg-white border border-red-200 p-4 text-sm text-red-800">
            <p className="font-semibold">You are about to delete your account.</p>
            <ul className="mt-2 list-disc list-inside space-y-0.5 text-red-700/90">
              <li>Your profile, watchlist, comparisons and messages will be removed</li>
              {user?.role === "developer" && <li>All your listed developments and their leads will be removed</li>}
              <li>You cannot recover this account or its data afterwards</li>
            </ul>
          </div>
          {requiresPwd && (
            <PasswordField label="Enter your password to confirm" testId="prof-delete-pwd" value={pwd} onChange={setPwd} />
          )}
          <div>
            <label className="text-xs font-semibold text-neutral-700">Type <span className="font-mono text-red-700">DELETE</span> to confirm</label>
            <input
              data-testid="prof-delete-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-500/15 outline-none transition bg-white font-mono"
              placeholder="DELETE"
            />
          </div>
          {err && <p data-testid="prof-delete-error" className="text-sm text-red-700 bg-white border border-red-200 rounded-xl px-3 py-2">{err}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => { setOpen(false); setErr(""); setPwd(""); setConfirmText(""); }} className="text-sm text-neutral-700 hover:text-neutral-900 px-4 py-2">Cancel</button>
            <button
              data-testid="prof-submit-delete-account"
              type="submit"
              disabled={busy || confirmText !== "DELETE" || (requiresPwd && !pwd)}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full px-5 py-2 font-semibold text-sm transition disabled:opacity-40"
            >
              {busy ? "Deleting..." : "Permanently delete account"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function PasswordField({ label, value, onChange, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-700">{label}</label>
      <div className="relative mt-1.5">
        <input
          data-testid={testId}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition pr-12"
          placeholder="••••••••"
        />
        <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700">
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-700">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition"
      />
    </div>
  );
}
